import { AuthType } from "@mcleod/core";
import { DataSource } from "../..";
import { ComponentPropDefinitions, ComponentPropDefinitionUtil } from "../../base/ComponentProps";
import { PropType } from "../../base/PropType";
import { ImagePropDefinitions } from "../image/ImageProps";
import { PanelPropDefinitions, PanelProps } from "../panel/PanelProps";
import { layoutSelector, stringArrayEditor } from "../textbox/TextboxProps";

export interface LayoutProps extends PanelProps {
  allowDecoratorOverride: boolean;
  auth: string | string[];
  defaultDecorator: string;
  helpLinks: string[];
  displayingFromToolbox: boolean;
  layoutName: string;
  mainDataSource: DataSource;
  needsServerLayout: boolean;
  applyFieldLevelPermissions: boolean;
  applyFieldLevelLicensing: boolean;
  title: string;
  titleImage: string;
  titleAddModeSuffix: string;
  titleEditModeSuffix: string;
  titlePlural: string;
  titleSearchModeSuffix: string;
  titleSingular: string;
  hideTitle: boolean;
  hideSuffix: boolean;
  inheritParentDataSource:string;
}

let layoutPropDefs: ComponentPropDefinitions;

export class LayoutPropDefinitions {
  public static getDefinitions(): ComponentPropDefinitions {
    if (layoutPropDefs == null) {
      layoutPropDefs = {
        ...PanelPropDefinitions.cloneDefs(),
        auth: {
          type: PropType.string,
          items: Object.keys(AuthType),
          description: "This specifies which user type(s) have access to this page."
        },
        allowDecoratorOverride: {
          type: PropType.bool,
          defaultValue: true,
          description: "This toggles whether the decorator for the page can be changed by providing a 'mode' parameter in the browser's URL."
        },
        displayingFromToolbox: {
          type: PropType.bool,
          defaultValue: false,
          description: "This property allows the onLoad of a layout to behave differently if the layout being loaded is being displayed from the toolbox.",
          visibleInDesigner: false
        },
        inheritParentDataSource: {
          type: PropType.string,
          items: ComponentPropDefinitionUtil.getDataSourceList,
          description: "Allows a nested layout to inherit a DataSource from the parent layout. The parent DataSource selected will be the nested layout's mainDataSource."
        },
        isNested: {
          type: PropType.bool,
          visibleInDesigner: false,
          description: "This specifies whether a layout is nested in another layout.  It is usually set automatically and shouldn't be set manually"
        },
        helpLinks: {
          type: PropType.string,
          editor: stringArrayEditor,
          description: "This specifies a list of links that are available for this layout."
        },
        layoutName: {
          type: PropType.string,
          editor: layoutSelector,
          description: "The name for this layout when nested."
        },
        mainDataSource: {
          type: PropType.string,
          items: ComponentPropDefinitionUtil.getDataSourceList,
          description: "This specifies which of the DataSources in this layout is considered the 'main' DataSource.  The 'main' DataSource will be attached to the toolbar or the list component when the page is decorated."
        },
        needsServerLayout: {
          type: PropType.bool,
          visibleInDesigner: false,
          defaultValue: true
        },
        defaultDecorator: {
          type: PropType.string,
          items: ["none", "list", "add", "search", "update", "advanced"],
          defaultValue: "none",
          description: "This specifies which 'decorator' will be used by default when this page is loaded.  Decorators can be changed if the URL for the page also includes a 'mode' parameter."
        },
        title: {
          type: PropType.string,
          affectsProps: ["titleAddModeSuffix", "titleEditModeSuffix", "titlePlural", "titleSearchModePrefix", "titleSingular"],
          description: "Specifies the title that will appear in the browser's window when this page is loaded.  Field names from the mai DataSource may be used (enclose in {})."
        },
        titleAddModeSuffix: {
          type: PropType.string,
          description: "Specifies text to append to the title when the screen is displayed in add mode.  Field names from the mainDataSource may be used (enclose in {})."
        },
        titleEditModeSuffix: {
          type: PropType.string,
          description: "Specifies text to append to the title when the screen is displayed in edit mode.  Field names from the mainDataSource may be used (enclose in {})."
        },
        titleImage: {
          type: PropType.string,
          editor: ImagePropDefinitions.imagePropertyEditor,
          description: "Specifies the image that will be used to represent this layout when it is shown in the menu and the title bar when shown in a dialog."
        },
        titlePlural: {
          type: PropType.string,
          affectsProps: ["titleAddModeSuffix", "titleEditModeSuffix", "titlePlural", "titleSearchModePrefix", "titleSingular"],
          description: "Specifies the title of this screen when it should appear in plural form."
        },
        titleSearchModeSuffix: {
          type: PropType.string,
          description: "Specifies text to append to the title when the screen is displayed in search mode.  Field names from the main DataSource may be used (enclose in {})."
        },
        titleSingular: {
          type: PropType.string,
          affectsProps: ["titleAddModeSuffix", "titleEditModeSuffix", "titlePlural", "titleSearchModePrefix", "titleSingular"],
          description: "Specifies the title of this screen when it should appear in singular form."
        },
        hideTitle: {
          type: PropType.bool,
          defaultValue: false,
          description: "If selected, the title will not be added to the title panel. The title settings will only be used for the document title."
        },
        hideSuffix: {
          type: PropType.bool,
          defaultValue: false,
          description: "If selected, the suffix will not be displayed in the title panel."
        }
      };
      layoutPropDefs.fillHeight.defaultValue = true;
      layoutPropDefs.fillRow.defaultValue = true;
      layoutPropDefs.scrollX.defaultValue = true;
      layoutPropDefs.scrollY.defaultValue = true;
      layoutPropDefs.padding.defaultValue = 0;
      delete layoutPropDefs.id;
      for (const [propName, prop] of Object.entries(layoutPropDefs))
        if (["Data"].includes(prop.category))
          delete layoutPropDefs[propName];
      ComponentPropDefinitionUtil.populateComponentPropNames(layoutPropDefs);
    }
    return layoutPropDefs;
  }
}
